import {
  documentEditRequestSchema,
  documentExportResponseSchema,
  documentModelResponseSchema,
  type DocumentEditRequestDTO,
  type DocumentExportRequestDTO,
} from '@shared/schemas/document-model';
import {
  documentGenerationAcceptedSchema,
  documentGenerationStatusResponseSchema,
  generatedDocumentVersionsResponseSchema,
  type DocumentGenerationKindDTO,
  type DocumentGenerationSpecDTO,
  type GeneratedDocumentVersionsQueryDTO,
} from '@shared/schemas/document-generation';
import { apiBaseUrl } from '@/lib/auth-client';
import { ApiError } from '@/lib/api-error';

type ApiEnvelope = {
  data?: unknown;
  error?: {
    code?: string;
    details?: Record<string, unknown>;
    message?: string;
  };
};

async function parseResponse(response: Response) {
  const payload = await response.json().catch(() => null) as ApiEnvelope | null;
  if (!response.ok || !payload) {
    throw new ApiError({
      code: payload?.error?.code,
      details: payload?.error?.details,
      message: payload?.error?.message ?? response.statusText ?? 'La requête a échoué.',
      status: response.status,
    });
  }
  return payload.data;
}

export async function createDocumentGeneration(input: {
  conversationId: string;
  kind: DocumentGenerationKindDTO;
  spec: DocumentGenerationSpecDTO;
}) {
  const response = await fetch(new URL(`/api/chat/conversations/${input.conversationId}/generations`, apiBaseUrl), {
    body: JSON.stringify(input),
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  return documentGenerationAcceptedSchema.parse(await parseResponse(response));
}

export async function getDocumentGeneration(conversationId: string, generationId: string) {
  const response = await fetch(
    new URL(`/api/chat/conversations/${conversationId}/generations/${generationId}`, apiBaseUrl),
    { credentials: 'include', method: 'GET' },
  );
  return documentGenerationStatusResponseSchema.parse(await parseResponse(response));
}

export async function getGeneratedDocumentVersions(
  conversationId: string,
  documentId: string,
  query: GeneratedDocumentVersionsQueryDTO,
) {
  const url = new URL(
    `/api/chat/conversations/${conversationId}/generated-documents/${documentId}/versions`,
    apiBaseUrl,
  );
  url.searchParams.set('page', String(query.page));
  url.searchParams.set('pageSize', String(query.pageSize));
  const response = await fetch(url, { credentials: 'include', method: 'GET' });
  return generatedDocumentVersionsResponseSchema.parse(await parseResponse(response));
}

export async function getDocumentGenerationModel(conversationId: string, generationId: string) {
  const response = await fetch(
    new URL(`/api/chat/conversations/${conversationId}/generations/${generationId}/model`, apiBaseUrl),
    { credentials: 'include', method: 'GET' },
  );
  return documentModelResponseSchema.parse(await parseResponse(response));
}

/** HTML d'aperçu du moteur blocks, destiné à l'attribut `srcdoc` d'une iframe sandboxée. */
export async function getDocumentGenerationPreviewHtml(conversationId: string, generationId: string) {
  const response = await fetch(
    new URL(`/api/chat/conversations/${conversationId}/generations/${generationId}/preview`, apiBaseUrl),
    { credentials: 'include', headers: { Accept: 'text/html' }, method: 'GET' },
  );
  if (!response.ok) {
    // Les erreurs métier sont renvoyées en JSON : on réutilise l'extraction de code.
    await parseResponse(response);
    throw new ApiError({ message: response.statusText || 'Aperçu indisponible.', status: response.status });
  }
  return response.text();
}

export async function createDocumentGenerationExport(
  conversationId: string,
  generationId: string,
  input: DocumentExportRequestDTO,
) {
  const response = await fetch(
    new URL(`/api/chat/conversations/${conversationId}/generations/${generationId}/exports`, apiBaseUrl),
    {
      body: JSON.stringify(input),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    },
  );
  return documentExportResponseSchema.parse(await parseResponse(response));
}

/** Crée une nouvelle version immuable à partir de `baseGenerationId` (réponse : génération `pending`). */
export async function submitDocumentEdits(
  conversationId: string,
  documentId: string,
  request: DocumentEditRequestDTO,
) {
  const body = documentEditRequestSchema.parse(request);
  const response = await fetch(
    new URL(`/api/chat/conversations/${conversationId}/generated-documents/${documentId}/edits`, apiBaseUrl),
    {
      body: JSON.stringify(body),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    },
  );
  return documentGenerationAcceptedSchema.parse(await parseResponse(response));
}
